import { useApiClient } from "@/hooks/useApiClient";
import { type Role } from "@/types/role";
import useSWR from "swr";

export interface CurrentUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  clientId: string | null;
  // API は `is_consortium_member` を返すが、axios-case-converter が camelCase へ変換するため
  // アプリ内では `isConsortiumMember` で受ける。所属クライアントが共同コンソーシアム企業なら true
  // （マイページ／ヘッダーのバッジ表示に使う）。
  isConsortiumMember: boolean;
  // 相続後リユース候補の月額会員か（BE: is_inheritance_reuse_monthly_member）。
  isInheritanceReuseMonthlyMember: boolean;
  // 役所地番地図レイヤーを利用できるクライアントか（BE: is_municipal_parcel_map_available）。
  isMunicipalParcelMapAvailable: boolean;
}

/**
 * 現在ユーザーと読み込み状態を返す。
 *
 * `user` は未取得（初回ロード中・未ログイン・取得失敗）で null。ロード中と「確定して user が無い」を
 * 区別したい箇所（月額会員限定ルートのガード等）は `isLoading` を併用する。ロード中に null を
 * 「非会員」と誤判定して画面から追い出さないため。
 */
export const useCurrentUserWithStatus = (): {
  user: CurrentUser | null;
  isLoading: boolean;
} => {
  const { apiClient } = useApiClient();
  const url = "/user/current";

  const fetcher = async (): Promise<CurrentUser> => {
    return (await apiClient.get<CurrentUser>(url)).data;
  };
  const { data: currentUser, isLoading } = useSWR(url, fetcher, {
    revalidateOnFocus: false,
  });

  return { user: currentUser ?? null, isLoading };
};

export const useCurrentUser = (): CurrentUser | null =>
  useCurrentUserWithStatus().user;
